<template>
  <div class="relative">
    <div class="bg-white border-b border-[#F3F3F3] tablet-h:border-[#E5E5E5]">
      <div
        class="h-[50px] tablet-h:h-[60px] px-3 tablet-h:px-5 py-1.5 tablet-h:py-3 flex items-center tablet-h:justify-center relative"
      >
        <div
          class="h-full tablet-h:absolute left-3 tablet-h:left-5 flex items-center"
        >
          <RouterLink
            v-if="origin"
            class="hidden tablet-h:block tablet-h:h-[30px]"
            to="/home"
          >
            <img
              v-if="origin.id !== 'Dacr'"
              :src="origin.logo.black.url"
              class="h-full"
            />
            <DacrWordLogo v-else class="h-full" />
          </RouterLink>
          <RouterLink
            v-if="!isSearchActive"
            class="tablet-h:hidden w-[42px] h-[42px] mr-3"
            to="/home"
          >
            <Icon name="Dacr" class="fill-gray-800" />
          </RouterLink>
        </div>
        <Search
          class="w-full tablet-h:min-w-[580px] tablet-h:max-w-[580px]"
          @active="handleSearchActive"
        />
        <div
          class="hidden tablet-h:flex items-center justify-end absolute right-3 tablet-h:right-5"
        >
          <a
            href="/download"
            class="hidden font-primary font-medium text-[15px] items-center hover:text-green-700 mr-6 cursor-pointer"
            :class="downloadClasses"
            target="_blank"
          >
            <div
              class="w-6 h-6 flex items-center justify-center rounded-md border border-gray-400/25 mr-2"
            >
              <Icon name="Dacr" :size="16" />
            </div>
            <div class="font-primary">Get app</div>
          </a>

          <Float.Base position="bottom" class="mr-6">
            <Float.Trigger>
              <div
                class="w-[26px] h-[26px] flex justify-center items-center cursor-pointer relative"
              >
                <Icon name="Notification" class="stroke-gray-800" />
                <div
                  v-if="showNotificationBadge"
                  class="w-[13px] h-[13px] border-[1.5px] border-white absolute -top-1 -right-[2px] bg-red-700 rounded-full"
                />
              </div>
            </Float.Trigger>
            <Float.Component>
              <Notifications />
            </Float.Component>
          </Float.Base>
          <Float.Base position="bottom">
            <Float.Trigger>
              <Avatar
                class="cursor-pointer"
                :src="user?.avatarUrl"
                :size="36"
              />
            </Float.Trigger>
            <Float.Component>
              <AccountMenu />
            </Float.Component>
          </Float.Base>
          <UiButton
            v-if="user && isBasicUser"
            class="ml-[22px]"
            primary
            @click="handleUpgradeClick"
          >
            Upgrade
          </UiButton>
        </div>
      </div>

      <div
        class="tablet-h:hidden w-full h-[50px] px-6 flex items-center justify-between"
      >
        <div
          class="w-[50px] h-[50px] flex flex-col items-center justify-center"
          @click="handleHomeClick"
        >
          <Icon
            :size="24"
            name="Home"
            class="mb-1"
            :class="getActiveIconClasses(0)"
          />
          <div
            class="font-primary font-medium text-[10px] leading-[12px] tracking-[-0.24px]"
            :class="getActiveLabelClasses(0)"
          >
            Home
          </div>
        </div>

        <div
          class="w-[50px] h-[50px] flex flex-col items-center justify-center"
          :class="uploadClasses"
          @click="handleUploadClick"
        >
          <Icon
            :size="24"
            name="Plus"
            class="mb-1"
            :class="getActiveIconClasses(1)"
          />
          <div
            class="font-primary font-medium text-[10px] leading-[12px] tracking-[-0.24px]"
            :class="getActiveLabelClasses(1)"
          >
            Upload
          </div>
        </div>

        <div
          class="w-[50px] h-[50px] flex flex-col items-center justify-center"
          @click="handleActivityClick"
        >
          <div class="w-6 h-6 flex items-center justify-center mb-1 relative">
            <Icon name="Notification" :class="getActiveIconClasses(2)" />
            <div
              v-if="showNotificationBadge"
              class="w-[13px] h-[13px] border-[1.5px] border-white absolute -top-1 -right-1 bg-red-700 rounded-full"
            />
          </div>
          <div
            class="font-primary font-medium text-[10px] leading-[12px] tracking-[-0.24px]"
            :class="getActiveLabelClasses(2)"
          >
            Activity
          </div>
        </div>

        <div
          class="w-[50px] h-[50px] flex flex-col items-center justify-center"
          @click="handleAccountClick"
        >
          <div class="w-6 h-6 flex items-center justify-center mb-1">
            <Avatar class="cursor-pointer" :src="user?.avatarUrl" :size="24" />
          </div>
          <div
            class="font-primary font-medium text-[10px] leading-[12px] tracking-[-0.24px] max-w-[50px] text-ellipsis overflow-hidden"
            :class="getActiveLabelClasses(3)"
          >
            You
          </div>
        </div>
      </div>
    </div>

    <Notifications
      v-if="showMobileNotification"
      class="tablet-h:hidden absolute z-90"
    />

    <MobileAccountOverview
      v-if="showMobileAccountOverview"
      class="tablet-h:hidden absolute z-90"
      @showSettings="handleShowSettings"
      @close="hideFloatingViews"
    />

    <div
      class="hidden tablet-h:flex w-full absolute top-[calc(100%+10px)] items-center justify-center px-3"
    >
      <Toast />
    </div>
  </div>
</template>

<script setup lang="ts">
import DacrWordLogo from '@/assets/svg/other/DacrWordLogo.svg'
import Search from '@/features/search/components/SearchBar.vue'
import { Float, UiButton, Avatar, Icon } from '@dacr/ui'
import Notifications from '@/shared/components/Notifications.vue'
import {
  useNotificationsQuery,
  UserPermission,
} from '@/shared/graphql/legacy/generated'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import AccountMenu from '@/shared/components/AccountMenu.vue'
import Toast from '@/shared/components/Toast.vue'
import { computed, ref, watch } from 'vue'
import { useUploadFlow } from '@/features/upload/composables/useUploadFlow'
import MobileAccountOverview from '@/shared/components/MobileAccountOverview.vue'
import { useUserStore } from '@/shared/stores/user'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { useOriginStore } from '@/shared/stores/origin'

const { result } = useNotificationsQuery(
  {},
  {
    pollInterval: 60_000,
  }
)

const route = useRoute()
const router = useRouter()
const dialog = useDialog()
const userStore = useUserStore()
const originStore = useOriginStore()
const { showUploadDialog } = useUploadFlow()

const isSearchActive = ref(false)
const showMobileNotification = ref(false)
const showMobileAccountOverview = ref(false)

const showNotificationBadge = ref(false)

watch(
  result,
  () => {
    showNotificationBadge.value =
      !!result.value?.notifications.unreadNotifications
  },
  {
    immediate: true,
  }
)

const user = computed(() => userStore.user)

const origin = computed(() => originStore.userOrigin)

const isBasicUser = computed(
  () => !user.value?.permissions.includes(UserPermission.Pro)
)

const activeMobileNavIndex = computed(() => {
  if (showMobileNotification.value) {
    return 2
  }

  if (route.path.includes('/settings') || showMobileAccountOverview.value) {
    return 3
  }

  if (route.path.includes('/home')) {
    return 0
  }

  return null
})

const downloadClasses = computed(() => ({
  'min-[1280px]:flex': isBasicUser.value,
  'desktop:flex': !isBasicUser.value,
}))

const getActiveIconClasses = (index: number) => {
  return {
    'h-full': true,
    'stroke-[#999]': index !== activeMobileNavIndex.value,
    'stroke-green-900': index === activeMobileNavIndex.value,
  }
}

const getActiveLabelClasses = (index: number) => {
  return {
    'text-[#999]': index !== activeMobileNavIndex.value,
    'text-green-900': index === activeMobileNavIndex.value,
  }
}

const canUpload = computed(() =>
  user.value?.permissions.includes(UserPermission.Upload)
)

const uploadClasses = computed(() => ({
  ...(!canUpload.value && {
    'pointer-events-none': true,
    'opacity-50': true,
  }),
}))

const handleSearchActive = (isActive: boolean) => {
  isSearchActive.value = isActive
}

const hideFloatingViews = () => {
  showMobileNotification.value = false
  showMobileAccountOverview.value = false
}

const handleHomeClick = () => {
  router.push('/home')
  hideFloatingViews()
}

const handleActivityClick = () => {
  hideFloatingViews()
  showMobileNotification.value = true
}

const handleAccountClick = () => {
  hideFloatingViews()
  showMobileAccountOverview.value = true
}

const handleShowSettings = () => {
  hideFloatingViews()
  router.push('/settings')
}

const handleUploadClick = () => {
  showUploadDialog()
}

const handleUpgradeClick = () => {
  dialog.open(Dialogs.UpgradeToPro)
}

onBeforeRouteUpdate((_, __, next) => {
  document.body.click()
  showMobileNotification.value = false

  next()
})
</script>
